import * as getCountryISO3 from 'country-iso-2-to-3';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { customFieldsDefaults } from '@/helpers/IdentityHelpers';
import { indexBy } from '@/helpers/IndexHelpers';
import { formInputPlaceholderLanguage, isTrueish } from '@/helpers/StringHelpers';
import AddressInput from '@/components/forms/AddressInput.vue';
import FormInput2 from '@/components/forms/FormInput2.vue';
import { addressFieldNames, alterAddressFieldsForAutocomplete, getAddressAutocompleteApiKey, supportedCountries as addressAutocompleteSupportedCountries, } from '../../helpers/AddressHelpers';
@Component({
    name: 'IdentityForm',
    components: { AddressInput, FormInput2 },
})
export default class default_1 extends Vue {
    @Prop({ required: true })
    value;
    @Prop({ default: () => [] })
    fields;
    @Prop({ default: false })
    showRequiredFieldsDescription;
    defaults = customFieldsDefaults();
    answers = this.defaults;
    inputModeManual = false;
    autocompletedAddressData = null;
    created() {
        const value = { ...this.value };
        // MyProfileRoute uses the @value prop for current values. In CheckoutRoute it is always an empty object: {}
        // Form state expects boolean true/false. Convert API checkbox values ('1' or '0' strings) to boolean.
        for (const field of this.fields) {
            if (value[field.key] !== undefined && field.type === 'checkbox') {
                // The API returns this as 1/0, we need to convert it to true/false
                value[field.key] = isTrueish(value[field.key]) ?? false;
            }
        }
        this.answers = value;
        // Set address data manually and separately, because this is managed separately from other answers
        this.autocompletedAddressData = {
            address: this.value.address,
            city: this.value.city,
            state: this.value.state,
            county: this.value.county,
            zipCode: this.value.zip_code,
        };
    }
    get fieldsDict() {
        return indexBy('key', this.fields);
    }
    @Watch('autocompletedAddressData')
    setAddressAnswers(data) {
        const setAnswer = (key, value) => {
            if (this.fieldsDict[key] && value !== undefined) {
                Vue.set(this.answers, key, value);
            }
        };
        setAnswer('address', data.address);
        setAnswer('city', data.city);
        setAnswer('state', data.state);
        setAnswer('region', data.state);
        setAnswer('county', data.county);
        setAnswer('zip_code', data.zipCode);
        if (data.zipCode) {
            this.zipCodeBlurred();
        }
    }
    get addressAutocompleteSupported() {
        const config = getAddressAutocompleteApiKey();
        if (config && this.fieldsDict.address && this.fieldsDict.country) {
            // TODO Offer for all countries?
            return addressAutocompleteSupportedCountries.has(this.countrySelected);
        }
        else {
            return false;
        }
    }
    get shouldAddressFieldBeRequired() {
        if (this.addressInputMode === 'manual') {
            return this.fieldsDict['address']?.required;
        }
        else {
            return addressFieldNames.some((name) => this.fieldsDict[name]?.required);
        }
    }
    get addressInputMode() {
        return this.addressAutocompleteSupported ? (this.inputModeManual ? 'manual' : 'autocomplete') : 'manual';
    }
    get displayFields() {
        const fields = this.addressInputMode === 'autocomplete' ? alterAddressFieldsForAutocomplete(this.fields) : this.fields;
        return fields.map((field) => ({
            ...field,
            placeholderLanguage: formInputPlaceholderLanguage(field),
        }));
    }
    get countrySelected() {
        // `getCountryISO3()` needs to input to be uppercase
        return getCountryISO3((this.answers.country ?? '').toUpperCase());
    }
    onBlur(e) {
        // Checkout applies discounts based on post code for some tenants.
        if (e.target.name === 'zip_code') {
            this.zipCodeBlurred();
        }
    }
    zipCodeBlurred() {
        const zipCode = this.answers.zip_code;
        if (zipCode?.length > 0) {
            this.$emit('onBlurZipCode', zipCode);
        }
    }
    @Watch('fields')
    onFieldsChange() {
        const answers = this.answersAfterFieldsChange(this.fields);
        const hasAnswerWithNoField = Object.keys(this.answers).some((key) => !this.fieldsDict[key]);
        const hasDifferentAmountOfAnswers = Object.values(answers).filter((answer) => answer).length !==
            Object.values(this.answers).filter((answer) => answer).length;
        if (hasDifferentAmountOfAnswers || hasAnswerWithNoField) {
            this.answers = answers;
        }
    }
    answersAfterFieldsChange(fields) {
        const answers = {};
        for (const field of fields) {
            // Answers are set to the defaults on initialisation. If a field is removed from the identity form and
            // then re-added then the default value will have been lost, so set it if there is no answer.
            const answer = this.answers[field.key] ?? this.defaults[field.key];
            // Don't set answers for disabled fields unless they are read-only.
            // If you don't set an answer then nothing will be displayed in the field.
            if ((!field.disabled || field.readOnly) && this.hasValidOptionValue(field, answer)) {
                answers[field.key] = answer;
            }
        }
        return answers;
    }
    hasValidOptionValue(field, answer) {
        const options = field.options;
        return !options || options.some((option) => option.value === answer);
    }
    @Watch('answers', { deep: true })
    onChange(data) {
        // There may be initial default values for fields that are not enabled.
        // Only emit values that are enabled.
        const result = {};
        for (const element of this.fields) {
            // Ignore confirmation fields like emailConfirm and passwordConfirm.
            if (!element.disabled && !element.readOnly && !element.mustEqual && !element.mustEqualCaseInsensitive) {
                result[element.key] = data[element.key];
            }
        }
        this.$emit('input', result);
    }
}
